import React, { useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Input, Modal, Pagination, Select, Space, Tooltip, Typography, message } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useUserContext from "../../../hooks/useUserContext";
import {
    CreateV2Template,
    DeleteV2Template,
    ListV2Templates,
    PreviewV2Template,
    RetryRejectedV2TemplateAsDraft,
    SubmitV2TemplateToTwilio,
    V2TemplateListItemDto,
} from "../../../api/v2/templates";
import Loader from "../../../components/loader/Loader";
import Page from "../../../components/page/Page";
import Panel from "../../../components/panel/Panel";
import TemplateThumbV2 from "../../../components/templateThumbV2/TemplateThumbV2";
import TemplateBuilderPage from "./TemplateBuilderPage";
import {
    getContentTypes,
    getDisplayStatus,
    getPreviewText,
    getRejectedReason,
    getTypeLabel,
    parseJsonMaybe,
} from "./templateV2Utils";
import "./templates-v2-page.css";

const { Text } = Typography;

const PAGE_SIZE = 12;

export default function TemplatesV2Page() {
    const { selectedDomain } = useUserContext();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<V2TemplateListItemDto[]>([]);
    const [searchValue, setSearchValue] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewId, setPreviewId] = useState<number | null>(null);
    const [previewTo, setPreviewTo] = useState("");
    const [previewSending, setPreviewSending] = useState(false);

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsRow, setDetailsRow] = useState<V2TemplateListItemDto | null>(null);
    const [builderPanelOpen, setBuilderPanelOpen] = useState(false);
    const [builderTemplateId, setBuilderTemplateId] = useState<number | null>(null);
    const [builderReadOnly, setBuilderReadOnly] = useState(false);
    const [builderStatus, setBuilderStatus] = useState<string | null>(null);
    const [builderTypeLabel, setBuilderTypeLabel] = useState<string | null>(null);
    const [builderSession, setBuilderSession] = useState(0);

    const openTemplatePanel = (templateId: number | null, readOnly: boolean, status?: string | null, typeLabel?: string | null) => {
        setBuilderTemplateId(templateId);
        setBuilderReadOnly(readOnly);
        setBuilderStatus(status ? String(status).toLowerCase() : null);
        setBuilderTypeLabel(typeLabel || null);
        setBuilderPanelOpen(true);
    };

    const load = async () => {
        if (!selectedDomain) return;
        setLoading(true);
        try {
            const res = await ListV2Templates(selectedDomain);
            if (!res.ok) {
                message.error(res.error || "Failed to load templates");
                setRows([]);
                return;
            }
            setRows(res.data?.templates || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDomain]);

    const submitTemplate = async (id: number) => {
        if (!selectedDomain) return;
        const key = `submit-${id}`;
        message.loading({ content: "Submitting...", key });
        try {
            const res = await SubmitV2TemplateToTwilio(selectedDomain, id);
            if (!res.ok) {
                message.error({ content: res.error || res.data?.reason || "Submit failed", key });
                return;
            }
            message.success({ content: "Submitted to Twilio for approval", key });
            await load();
        } catch (err: any) {
            message.error({ content: err?.message || "Submit failed", key });
        }
    };

    const deleteTemplate = async (id: number) => {
        if (!selectedDomain) return;
        const key = `delete-${id}`;
        message.loading({ content: "Deleting...", key });
        try {
            const res = await DeleteV2Template(selectedDomain, id);
            if (!res.ok) {
                message.error({ content: res.error || res.data?.reason || "Delete failed", key });
                return;
            }
            message.success({ content: "Template deleted", key });
            await load();
        } catch (err: any) {
            message.error({ content: err?.message || "Delete failed", key });
        }
    };

    const retryRejectedAsDraft = async (id: number) => {
        if (!selectedDomain) return;
        const key = `retry-${id}`;
        message.loading({ content: "Resetting to draft...", key });
        try {
            const res = await RetryRejectedV2TemplateAsDraft(selectedDomain, id);
            if (!res.ok) {
                message.error({ content: res.error || res.data?.reason || "Retry failed", key });
                return;
            }
            message.success({ content: "Template moved to draft", key });
            await load();
        } catch (err: any) {
            message.error({ content: err?.message || "Retry failed", key });
        }
    };

    const duplicateTemplate = async (row: V2TemplateListItemDto) => {
        if (!selectedDomain) return;
        const key = `duplicate-${row.id}`;
        message.loading({ content: "Duplicating...", key });
        try {
            const content_types = Array.isArray(parseJsonMaybe(row.content_types_json)) ? parseJsonMaybe(row.content_types_json) : [];
            const types_config = parseJsonMaybe(row.types_config_json) || {};
            const variables_schema = Array.isArray(parseJsonMaybe(row.variables_schema_json)) ? parseJsonMaybe(row.variables_schema_json) : [];
            const metadata = parseJsonMaybe(row.metadata_json) || {};
            const nextTitleBase = String(row.title || "Template").trim() || "Template";

            const res = await CreateV2Template(selectedDomain, {
                channel: "whatsapp",
                provider: "twilio",
                title: `${nextTitleBase} (copy)`,
                language: "en",
                category: (String(row.category || "UTILITY").toUpperCase() as "MARKETING" | "UTILITY"),
                content_types,
                types_config,
                variables_schema,
                metadata,
            });

            if (!res.ok) {
                message.error({ content: res.error || "Duplicate failed", key });
                return;
            }

            message.success({ content: `Duplicated as draft (#${res.data?.id})`, key });
            await load();
        } catch (err: any) {
            message.error({ content: err?.message || "Duplicate failed", key });
        }
    };

    const openPreview = (id: number) => {
        setPreviewId(id);
        setPreviewTo("");
        setPreviewOpen(true);
    };

    const sendPreview = async () => {
        if (!selectedDomain || !previewId) return;
        if (!previewTo.trim()) {
            message.error("Please enter a WhatsApp number (E.164)");
            return;
        }
        setPreviewSending(true);
        try {
            const res = await PreviewV2Template(selectedDomain, previewId, previewTo.trim());
            if (!res.ok) {
                message.error(res.error || res.data?.reason || "Preview failed");
                return;
            }
            message.success("Preview sent");
            setPreviewOpen(false);
        } finally {
            setPreviewSending(false);
        }
    };

    const filteredRows = useMemo(() => {
        const q = searchValue.trim().toLowerCase();
        const next = rows.filter((row) => {
            const displayStatus = String(getDisplayStatus(row) || "").toLowerCase();
            if (statusFilter !== "all" && displayStatus !== statusFilter) return false;
            if (!q) return true;
            const title = String(row.title || "").toLowerCase();
            const category = String(row.category || "").toLowerCase();
            const typeLabel = getTypeLabel(row).toLowerCase();
            return title.includes(q) || category.includes(q) || typeLabel.includes(q);
        });
        return next;
    }, [rows, searchValue, statusFilter]);

    const pagedRows = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredRows.slice(start, start + PAGE_SIZE);
    }, [filteredRows, page]);

    useEffect(() => {
        setPage(1);
    }, [searchValue, statusFilter]);

    const renderThumbActions = (row: V2TemplateListItemDto) => {
        const id = Number(row.id);
        const status = String(row.status || "").toLowerCase();
        const contentTypes = getContentTypes(row);
        const isMenu = contentTypes.includes("twilio/list-picker");
        const canSubmit = status !== "approved";
        const hasContent = !!row.provider_content_sid;
        const canPreview = status === "approved" || (isMenu && hasContent);

        if (status === "rejected") {
            return (
                <Dropdown
                    trigger={["click"]}
                    menu={{
                        items: [
                            { key: "retry", label: "Try again" },
                            { type: "divider" },
                            { key: "delete", label: "Delete", danger: true },
                        ],
                        onClick: ({ key, domEvent }) => {
                            domEvent?.stopPropagation();
                            if (key === "retry") {
                                Modal.confirm({
                                    title: "Try again?",
                                    content: "This resets the template to draft and removes the rejected Twilio content. The next submit will create a new Twilio template.",
                                    okText: "Try again",
                                    cancelText: "Cancel",
                                    onOk: () => retryRejectedAsDraft(id),
                                });
                                return;
                            }
                            if (key === "delete") {
                                Modal.confirm({
                                    title: "Delete template?",
                                    content: "This will delete the template from Twilio (if created) and from the database.",
                                    okText: "Delete",
                                    okButtonProps: { danger: true },
                                    cancelText: "Cancel",
                                    onOk: () => deleteTemplate(id),
                                });
                            }
                        },
                    }}
                >
                    <Button size="small" onClick={(e) => e.stopPropagation()} icon={<MoreOutlined />} />
                </Dropdown>
            );
        }

        return (
            <Dropdown
                trigger={["click"]}
                menu={{
                    items: [
                        {
                            key: "submit",
                            label: isMenu ? "Create Content" : "Submit",
                            disabled: !canSubmit || (isMenu && hasContent),
                        },
                        {
                            key: "edit",
                            label: "Edit",
                            disabled: status !== "draft",
                        },
                        { key: "duplicate", label: "Duplicate" },
                        { key: "preview", label: "Preview", disabled: !canPreview },
                        { type: "divider" },
                        { key: "delete", label: "Delete", danger: true },
                    ],
                    onClick: ({ key, domEvent }) => {
                        domEvent?.stopPropagation();
                        if (key === "submit") {
                            void submitTemplate(id);
                            return;
                        }
                        if (key === "edit") {
                            openTemplatePanel(id, false, status, getTypeLabel(row));
                            return;
                        }
                        if (key === "duplicate") {
                            void duplicateTemplate(row);
                            return;
                        }
                        if (key === "preview") {
                            openPreview(id);
                            return;
                        }
                        if (key === "delete") {
                            Modal.confirm({
                                title: "Delete template?",
                                content: "This will delete the template from Twilio (if created) and from the database.",
                                okText: "Delete",
                                okButtonProps: { danger: true },
                                cancelText: "Cancel",
                                onOk: () => deleteTemplate(id),
                            });
                        }
                    },
                }}
            >
                <Button size="small" onClick={(e) => e.stopPropagation()} icon={<MoreOutlined />} />
            </Dropdown>
        );
    };

    if (!selectedDomain) return null;

    return (
        <Page
            title="Templates"
            subtitle="Create and manage V2 WhatsApp templates."
            className="v2-templates-page"
            primaryAction={{
                label: "New Template",
                action: () => openTemplatePanel(null, false, null, null),
            }}
            secondaryActions={[{
                label: "Refresh",
                action: load,
            }]}
        >
            <div className="v2-templates-filters-bar">
                <Input
                    className="v2-templates-search"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search templates"
                />
                <Select
                    className="v2-templates-status-filter"
                    value={statusFilter}
                    onChange={(value) => setStatusFilter(String(value))}
                    options={[
                        { value: "all", label: "All Statuses" },
                        { value: "draft", label: "Draft" },
                        { value: "pending", label: "Pending" },
                        { value: "approved", label: "Approved" },
                        { value: "ready", label: "Ready" },
                        { value: "rejected", label: "Rejected" },
                    ]}
                />
            </div>

            {loading ? (
                <div className="v2-templates-loading"><Loader size={72} isFullPage={false} /></div>
            ) : (
                <>
                    <div className="v2-templates-grid">
                        {pagedRows.map((row, index) => {
                            const displayStatus = getDisplayStatus(row);
                            const rejectedReason = getRejectedReason(row);
                            const thumb = (
                                <TemplateThumbV2
                                    key={`template-${row.id}`}
                                    item={row}
                                    previewText={getPreviewText(row)}
                                    onClick={() => openTemplatePanel(
                                        Number(row.id),
                                        String(row.status || "").toLowerCase() !== "draft",
                                        String(row.status || "").toLowerCase(),
                                        getTypeLabel(row)
                                    )}
                                    index={index}
                                    actions={renderThumbActions(row)}
                                />
                            );

                            if (rejectedReason && String(displayStatus).toLowerCase() === "rejected") {
                                return (
                                    <Tooltip key={`template-tip-${row.id}`} title={rejectedReason}>
                                        {thumb}
                                    </Tooltip>
                                );
                            }

                            return thumb;
                        })}
                    </div>

                    <div className="v2-templates-footer">
                        <Text className="v2-templates-counter">{filteredRows.length} templates</Text>
                        <Pagination
                            current={page}
                            pageSize={PAGE_SIZE}
                            total={filteredRows.length}
                            onChange={setPage}
                            showSizeChanger={false}
                        />
                    </div>
                </>
            )}

            <Modal
                open={previewOpen}
                onCancel={() => setPreviewOpen(false)}
                onOk={sendPreview}
                okButtonProps={{ loading: previewSending }}
                title="Send Preview"
            >
                <Input
                    placeholder="WhatsApp number in E.164 (e.g. +14155552671)"
                    value={previewTo}
                    onChange={(e) => setPreviewTo(e.target.value)}
                />
                <Text type="secondary">
                    Preview requires approved status. Menus require created content and may fail outside the 24h window.
                </Text>
            </Modal>

            <Panel
                isOpen={builderPanelOpen}
                onClose={() => {
                    setBuilderPanelOpen(false);
                    setBuilderTemplateId(null);
                    setBuilderReadOnly(false);
                    setBuilderStatus(null);
                    setBuilderTypeLabel(null);
                    setBuilderSession((v) => v + 1);
                }}
                width={600}
                className="v2-templates-builder-panel"
                darkenScreen
            >
                {(builderStatus || builderTypeLabel) ? (
                    <div className="v2-template-panel-top-labels">
                        {builderTypeLabel ? (
                            <div className="v2-template-panel-type-label">
                                {builderTypeLabel}
                            </div>
                        ) : null}
                        {builderStatus ? (
                            <div className={`v2-template-panel-status-label ${builderStatus}`}>
                                {builderStatus}
                            </div>
                        ) : null}
                    </div>
                ) : null}
                <TemplateBuilderPage
                    key={`tpl-builder-${builderSession}-${builderTemplateId ?? "new"}-${builderReadOnly ? "ro" : "rw"}`}
                    embedded
                    templateIdOverride={builderTemplateId}
                    readOnlyOverride={builderReadOnly}
                    onTypeLabelChange={setBuilderTypeLabel}
                    onSaved={() => {
                        setBuilderPanelOpen(false);
                        setBuilderTemplateId(null);
                        setBuilderReadOnly(false);
                        setBuilderStatus(null);
                        setBuilderTypeLabel(null);
                        setBuilderSession((v) => v + 1);
                        void load();
                    }}
                />
            </Panel>
        </Page>
    );
}
